import React, { FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import { Ownership } from '../../@types/ownership'
import { RealEstateType } from '../../@types/realEstateType'
import { Button } from '../../components/styled/Button/Button'
import { useReportViewEvent, useResponsive } from '../../hooks'
import { Intent } from '../../@types/intent'
import { updateOwnership } from '../../store/lead/leadSlice'
import { Progress } from '../../@types/progress'
import { reportTrackingEvent } from '../../tracking/thunks'
import { OwnerStepIcon } from '../../components/icons/StepIcons/OwnerStepIcon'
import { IntegrationType } from '../../@types/config'
import { ReduxState } from '../../store/reducers'

interface OwnershipStepProps {
  goToNextStep: () => unknown
  goToPreviousStep: () => unknown
  progress: Progress
}

interface HeadlineProps {
  intent?: Intent
  realEstateType?: RealEstateType
}

const mobileButtons = {
  [Ownership.YES]: 'Ja',
  [Ownership.CO_OWNER]: 'Ich bin Teil-Eigentümer',
  [Ownership.RELATIVE]: 'Ich bin Angehöriger',
  [Ownership.NO]: 'Nein',
}

const tabletOrDesktopButtons = {
  [Ownership.YES]: 'Ich bin Eigentümer',
  [Ownership.CO_OWNER]: 'Ich bin Teil-Eigentümer',
  [Ownership.RELATIVE]: 'Ich bin Angehöriger',
  [Ownership.NO]: 'Ich bin nicht Eigentümer',
}

const rentOutFlowButtons = {
  [Ownership.YES]: 'Ja',
  [Ownership.NO]: 'Nein',
}

const Headline: FC<HeadlineProps> = ({ realEstateType, intent }) => {
  let theLandOrRealEstate
  if ([Intent.BUY, Intent.RENT].includes(intent as Intent)) {
    const firstPartOfSentence = 'Bist du bereits Eigentümer'
    switch (realEstateType) {
      case RealEstateType.LAND:
        theLandOrRealEstate = `${firstPartOfSentence} eines Grundstücks?`
        break
      case RealEstateType.APARTMENT:
        theLandOrRealEstate = `${firstPartOfSentence} einer Wohnung?`
        break
      case RealEstateType.HOUSE:
        theLandOrRealEstate = `${firstPartOfSentence} eines Hauses?`
        break
      default:
        theLandOrRealEstate = `${firstPartOfSentence} einer Immobilie?`
    }
  } else {
    const firstPartOfSentence = 'Bist du Eigentümer'
    switch (realEstateType) {
      case RealEstateType.LAND:
        theLandOrRealEstate = `${firstPartOfSentence} des Grundstücks?`
        break
      case RealEstateType.APARTMENT:
        theLandOrRealEstate = `${firstPartOfSentence} der Wohnung?`
        break
      case RealEstateType.HOUSE:
        theLandOrRealEstate = `${firstPartOfSentence} des Hauses?`
        break
      default:
        theLandOrRealEstate = `${firstPartOfSentence} der Immobilie?`
    }
  }
  return <>{theLandOrRealEstate}</>
}

export const OwnershipStep: FC<OwnershipStepProps> = (props) => {
  useReportViewEvent({ pageTitle: 'ownership' })
  const { isMobile } = useResponsive()
  const {
    config: { theme, integrationType },
    lead: { intent, realEstateType },
  } = useSelector((state: ReduxState) => state)
  const isIv24Integration = integrationType === IntegrationType.IMMOVERKAUF24

  const dispatch = useDispatch()

  const goToNextStep = (ownership: Ownership) => {
    dispatch(updateOwnership(ownership))
    dispatch(
      reportTrackingEvent({
        pageTitle: 'ownership',
        label: 'ownership_submit',
        inputValue: ownership,
        eventName: 'lead_input',
        customDimensions: {
          ga_cd_lead_engine_input_value: ownership,
        },
      })
    )
    props.goToNextStep()
  }

  return (
    <StepTemplate
      goToPreviousStep={props.goToPreviousStep}
      headline={<Headline realEstateType={realEstateType} intent={intent} />}
      icon={<OwnerStepIcon />}
      displayNext={false}
      progress={props.progress}
      backgroundColor={theme.background}
      textColor={theme.text}
    >
      {intent === Intent.LET
        ? Object.entries(rentOutFlowButtons).map(([ownership, label]) => (
            <Button
              key={ownership}
              text={label}
              onClick={() => goToNextStep(ownership as Ownership)}
              className='margin-right-m margin-bottom-xl'
              dataTesting={`option-${ownership}`}
              data-testid='ownership-button'
              backgroundColor={isIv24Integration ? theme?.secondary : theme.background}
              color={isIv24Integration ? '#FFFFFF' : theme.text}
              borderColor={theme?.secondary}
            />
          ))
        : Object.entries(isMobile ? mobileButtons : tabletOrDesktopButtons).map(([ownership, label]) => (
            <Button
              key={ownership}
              text={label}
              onClick={() => goToNextStep(ownership as Ownership)}
              className='margin-right-m margin-bottom-xl'
              dataTesting={`option-${ownership}`}
              data-testid='ownership-button'
              backgroundColor={isIv24Integration ? theme.secondary : theme.background}
              color={isIv24Integration ? '#FFFFFF' : theme.text}
              borderColor={theme?.secondary}
            />
          ))}
    </StepTemplate>
  )
}

export default OwnershipStep
